import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faFolderPlus } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { ISpecificBrickSettingsComponent } from 'src/app/components/dashboard/brick-settings/brick-specific-type-settings/ISpecificBrickSettingsComponent';
import { PresentationType } from 'src/app/samwebDescription/samweb/settings/presentationType';
import { TabPositionType } from 'src/app/samwebDescription/samweb/settings/tabBrick/tabPositionType';
import {
   ChangeBrickDefinitionMenuOpeningStateForBrickTab,
   ChangeBrickPosition,
   ChangeTabOrderForTabBrick,
   ChangeTabPresentation,
   ToggleBrickDefinitionMenuOpeningStateForBrickTab,
} from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/tab-brick-settings/tab-brick-settings.actions';
import { TabBrickSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/tab-brick-settings/tab-brick-settings.state';
import { TabContentRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-tab-brick-settings',
   templateUrl: './tab-brick-settings.component.html',
   styleUrls: ['./tab-brick-settings.component.scss'],
})
export class TabBrickSettingsComponent implements OnInit, AfterViewInit, OnDestroy, ISpecificBrickSettingsComponent {
   public brickInstanceId: string;

   public tabBrickPositionPossibleValues = TabPositionType.values();
   public tabPresentationTypePossibleValues = PresentationType.values();

   @ViewChild('brickSelectionTooltipDirective')
   public brickSelectionTooltipDirective: TooltipDirective;

   @ViewChild('brickSelectionPopupControl', { read: ElementRef })
   public brickSelectionPopupControl: ElementRef;

   public tabPosition$: Observable<TabPositionType>;
   public tabPresentationType$: Observable<PresentationType>;
   public tabIds$: Observable<string[]>;

   @Select(TabBrickSettingsState.isBrickDefinitionMenuOpen)
   public isBrickDefinitionMenuOpen$: Observable<boolean>;

   public addTabIcon: IconDefinition = faFolderPlus;

   private destroy$ = new Subject();

   constructor(private store: Store) {}

   ngOnInit() {
      this.tabPresentationType$ = this.store.select(TabBrickSettingsState.getPresentationType).pipe(map((fn) => fn(this.brickInstanceId)));
      this.tabPosition$ = this.store.select(TabBrickSettingsState.getTabPositionType).pipe(map((fn) => fn(this.brickInstanceId)));
      this.tabIds$ = this.store.select(TabBrickSettingsState.getTabs).pipe(
         map((fn) => fn(this.brickInstanceId)),
         map((taps: TabContentRemoteDto[]) =>
            _(taps)
               .map((x: TabContentRemoteDto) => x.tabId)
               .value()
         ),
         distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
         tap((x) => console.log(x))
      );
   }

   changeTabPosition(tabPosition: TabPositionType) {
      this.store.dispatch(new ChangeBrickPosition(this.brickInstanceId, tabPosition));
   }

   changeTabPresentationType(value: PresentationType) {
      this.store.dispatch(new ChangeTabPresentation(this.brickInstanceId, value));
   }

   public onDrop(event: CdkDragDrop<string[]>): void {
      if (event.previousIndex === event.currentIndex) {
         return;
      }
      this.store.dispatch(new ChangeTabOrderForTabBrick(this.brickInstanceId, event.previousIndex, event.currentIndex));
   }

   toggleBrickSelectionDialog() {
      this.store.dispatch(new ToggleBrickDefinitionMenuOpeningStateForBrickTab(this.brickInstanceId));
   }

   closeBrickSelectionDialog() {
      this.store.dispatch(new ChangeBrickDefinitionMenuOpeningStateForBrickTab(this.brickInstanceId, false));
   }

   ngAfterViewInit(): void {
      this.isBrickDefinitionMenuOpen$
         .pipe(
            tap((isOpen) => {
               if (_.isNil(this.brickSelectionTooltipDirective)) {
                  return;
               }
               if (!isOpen) {
                  this.brickSelectionTooltipDirective.hide();
                  return;
               }
               if (!_.isNil(this.brickSelectionPopupControl)) {
                  this.brickSelectionTooltipDirective.toggle(this.brickSelectionPopupControl.nativeElement, isOpen);
                  return;
               }
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
